import type { Client, FormattedExecutionResult, Sink, SubscribePayload } from 'graphql-ws';
import { API_BASE_URL } from './ApiService';
import { requestAccessToken } from './accessToken';
import { afterBootRequests } from './bootRequests';
import {
  desktopRealtimeClientFor,
  isRealtimeRefused,
  onRealtimeRefusedChange,
  MAIN_REALTIME_CONNECTION,
  REALTIME_REFUSED_CLOSE_CODE,
  setRealtimeConnectionState,
  setRealtimeRefused,
} from './realtimeClients';

/**
 * T-0770 (`docs/concept-mobile-app.md`, LC-6b): the tab's one realtime socket, in a module of its
 * own. The launch opens subscriptions (the attention stream, the work areas, a pairing) and signs
 * out through it, and none of that needs the rest of the work-item API, which the launch entry no
 * longer carries.
 */
function graphqlWsUrl(): string {
  const url = new URL(`${API_BASE_URL}/graphql`, window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/**
 * One socket per tab, shared by every subscription.
 *
 * Each `subscribeToEvents` call used to build its own `graphql-ws` client, so a tab paid a
 * WebSocket, a connection handshake and a server-side listener for every view that wanted live
 * updates — and the server counts those listeners per topic. Multiplexing over a single lazy
 * client keeps the count at one socket regardless of how many views are open; `graphql-ws`
 * already fans several subscriptions down one connection.
 */
const REALTIME_RETRY_CAP_MS = 30_000;
let realtimeClient: Client | null = null;

/** The main connection's socket state; a custom connection's is its own (`realtimeClients.ts`). */
function setRealtimeConnected(connected: boolean): void {
  setRealtimeConnectionState(MAIN_REALTIME_CONNECTION, connected);
}

/**
 * T-0559: `graphql-ws` is loaded when the first subscription opens, not parsed in the entry by every
 * launch. One load, shared by every caller. I-1380: not before the boot has sent its session check
 * and asked for the launch's code, which a store opening at start-up would otherwise be ahead of.
 */
let realtimeModule: Promise<typeof import('graphql-ws')> | null = null;

function loadRealtimeModule(): Promise<typeof import('graphql-ws')> {
  realtimeModule ??= afterBootRequests()
    .then(() => import('graphql-ws'))
    .catch((error: unknown) => {
      // A failed load (a deployment replaced under the tab) is tried again by the next subscription.
      realtimeModule = null;
      throw error;
    });
  return realtimeModule;
}

/**
 * A subscription on the tab's one socket. Returns its dispose at once; until the client's code is
 * in, disposing cancels the subscription before it is opened.
 */
export function realtimeSubscribe<T>(
  payload: SubscribePayload,
  sink: Sink<FormattedExecutionResult<T>>,
): () => void {
  // A credential refused for its kind (4430) stays refused: nothing is opened, so nothing retries. A
  // different credential (the view ended, a fresh sign-in) is a fresh start.
  releaseRealtimeRefusalIfCredentialChanged();
  if (isRealtimeRefused()) return parkWhileRefused(payload, sink);
  if (realtimeClient) return realtimeClient.subscribe<T>(payload, sink);
  let disposeOpened: (() => void) | null = null;
  let cancelled = false;
  loadRealtimeModule().then(
    ({ createClient }) => {
      if (!cancelled && !isRealtimeRefused())
        disposeOpened = realtimeGraphqlClient(createClient).subscribe<T>(payload, sink);
    },
    (error: unknown) => {
      if (!cancelled) sink.error(error);
    },
  );
  return () => {
    cancelled = true;
    disposeOpened?.();
  };
}

/** The library's own test for a close event (its default `shouldRetry`), kept here so it stays lazy. */
function isLikeCloseEvent(event: unknown): boolean {
  const close = event as { code?: unknown; reason?: unknown; wasClean?: unknown } | null;
  return (
    typeof close === 'object' &&
    close !== null &&
    typeof close.code === 'number' &&
    typeof close.reason === 'string' &&
    typeof close.wasClean === 'boolean'
  );
}

interface IParkedSubscription {
  payload: SubscribePayload;
  sink: Sink<FormattedExecutionResult<never>>;
  dispose: (() => void) | null;
}
const parked = new Set<IParkedSubscription>();

/**
 * A subscription asked for while the credential is refused opens nothing now. It is kept, so that when the
 * refusal lifts (the credential changed) it opens on the new one and its owner never has to know.
 */
function parkWhileRefused<T>(
  payload: SubscribePayload,
  sink: Sink<FormattedExecutionResult<T>>,
): () => void {
  const entry: IParkedSubscription = {
    payload,
    sink: sink as unknown as Sink<FormattedExecutionResult<never>>,
    dispose: null,
  };
  parked.add(entry);
  return () => {
    parked.delete(entry);
    entry.dispose?.();
  };
}

onRealtimeRefusedChange(() => {
  if (isRealtimeRefused()) return;
  const waiting = Array.from(parked);
  parked.clear();
  waiting.forEach((entry) => {
    entry.dispose = realtimeSubscribe(entry.payload, entry.sink);
  });
});

/** The token the refused socket carried; a different one is another credential. */
let refusedToken: string | null = null;

/**
 * W2: leaving "view as another role" swaps the tab's token without a sign-out. A refusal belongs to the
 * credential that was refused, so when the token is another one the refusal is dropped, the shared client
 * with it, and the streams reopen on the new credential.
 */
export function releaseRealtimeRefusalIfCredentialChanged(): void {
  if (isRealtimeRefused() && requestAccessToken() !== refusedToken) disposeRealtimeClient();
}

function isRefusedClose(event: unknown): boolean {
  return (
    typeof event === 'object' &&
    event !== null &&
    (event as { code?: unknown }).code === REALTIME_REFUSED_CLOSE_CODE
  );
}

function realtimeGraphqlClient(
  createClient: (typeof import('graphql-ws'))['createClient'],
): Client {
  if (realtimeClient) return realtimeClient;
  realtimeClient = createClient({
    url: graphqlWsUrl(),
    connectionParams: () => {
      const token = requestAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    // The socket is the doorbell for every view in the tab; giving up on it after three attempts
    // silently demotes the whole app to interval polling. Callers still keep a poll as a backstop,
    // and `shouldRetry` defaults to "unless the close was fatal", so an auth rejection still ends
    // the attempt rather than looping.
    retryAttempts: Number.MAX_SAFE_INTEGER,
    // The default backoff is an uncapped 2^n seconds, so with unlimited attempts a long outage
    // would push the next attempt days out and the tab would never come back. Cap it at half a
    // minute, jittered so a restarted server does not take every tab's reconnect at once.
    retryWait: (retries) =>
      new Promise((resolve) =>
        setTimeout(
          resolve,
          Math.min(REALTIME_RETRY_CAP_MS, 1_000 * 2 ** retries) + Math.floor(Math.random() * 1_000),
        ),
      ),
    // 4430 (a kind refusal) is final for this credential; every other close, a lapsed token's 4403 included,
    // keeps graphql-ws's default and retries with the token `connectionParams` reads again.
    shouldRetry: (errOrCloseEvent) =>
      isLikeCloseEvent(errOrCloseEvent) && !isRefusedClose(errOrCloseEvent),
    on: {
      connected: () => setRealtimeConnected(true),
      closed: (event) => {
        if (isRefusedClose(event)) {
          refusedToken = requestAccessToken();
          setRealtimeRefused(true);
        }
        setRealtimeConnected(false);
      },
      error: () => setRealtimeConnected(false),
    },
  });
  return realtimeClient;
}

export { isRealtimeConnected, onRealtimeConnectionChange } from './realtimeClients';

/**
 * A subscription for `workAreaId`: on the tab's one socket for a work area on the main connection,
 * through the desktop relay for one bound to a custom connection (T-0549, RTC-1).
 */
export function realtimeSubscribeFor<T>(
  workAreaId: string,
  payload: SubscribePayload,
  sink: Sink<FormattedExecutionResult<T>>,
): () => void {
  const desktop = desktopRealtimeClientFor(workAreaId);
  return desktop ? desktop.subscribe<T>(payload, sink) : realtimeSubscribe<T>(payload, sink);
}

/**
 * A subscription outside the work-item stream on the tab's one socket (F-0332: a pairing request
 * the person is approving), so a feature never opens a second WebSocket of its own.
 */
export function subscribeOnRealtimeSocket<T>(
  payload: { query: string; variables?: Record<string, unknown> },
  sink: Sink<FormattedExecutionResult<T>>,
): () => void {
  return realtimeSubscribe<T>(payload, sink);
}

/** Drops the shared socket — on sign-out, so the next connection re-reads the token. */
export function disposeRealtimeClient(): void {
  const client = realtimeClient;
  realtimeClient = null;
  refusedToken = null;
  setRealtimeRefused(false);
  setRealtimeConnected(false);
  void client?.dispose();
}
